import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  BarChart3,
  GitPullRequestArrow
} from "lucide-react";
import { Sidebar, SidebarContent, SidebarHeader, SidebarFooter, SidebarMenu, SidebarMenuItem, SidebarMenuButton } from "./ui/sidebar";
import { useAuthStore } from "../store/useAuthStore";
import { useStreemLogout } from "../lib/api";
import { NavMain } from "./nav-main";
import { NavUser } from "./nav-user";
import { ThemeToggle } from "./theme-toggle";

interface AppSidebarProps {
  variant?: "sidebar" | "floating" | "inset";
}

export function AppSidebar({ variant = "sidebar" }: AppSidebarProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { mutateAsync: logout } = useStreemLogout();

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
      // Even if logout fails, clear local state and redirect
      useAuthStore.getState().logout();
      window.location.href = "/";
    }
  };

  // Handle navigation
  const handleNavigate = (url: string) => {
    if (url.startsWith('/')) {
      navigate(url);
    }
  };

  // Get current path
  const currentPath = window.location.pathname;

  // Main navigation items
  const mainNavItems = [
    {
      title: "Workflows",
      url: "/workflows",
      icon: BarChart3,
      isActive: currentPath === "/workflows" || currentPath.includes("/workflow")
    },
    {
      title: "Logs",
      url: "/logs",
      icon: FileText,
      isActive: currentPath === "/logs"
    }
  ];

  // No document items or secondary navigation items needed

  // User data
  const userData = {
    name: user ? `${user.firstName} ${user.lastName}` : "User",
    email: user?.email || "user@example.com",
    avatar: "" // We don't have avatar in the User type, so we'll use an empty string
  };

  return (
    <Sidebar collapsible="icon" variant={variant}>
      <SidebarHeader className="border-b">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <GitPullRequestArrow className="!size-5" />
              <span className="text-base font-semibold">Migration Utility</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {/* Main navigation */}
        <NavMain 
          items={mainNavItems} 
          onNavigate={handleNavigate}
        />
      </SidebarContent>
      <SidebarFooter className="border-t">
        {/* Theme toggle */}
        <ThemeToggle />
        {/* User profile */}
        <NavUser
          user={userData}
          onLogout={handleLogout}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
